import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Button, Placeholder, Text } from "@telegram-apps/telegram-ui";
import { useNavigate } from "react-router-dom";
import ApiService from "../../handlers/apiService";
import { TaskGroupEnum, TaskTypeEnum, UserTask } from "../../handlers/types";
import {
  addToHomeScreen,
  openTelegramLink,
  hapticFeedback,
} from "@telegram-apps/sdk";
import { useI18n } from "../../utils/i18n/I18nProvider";
import CheckIcon from "./CheckIcon";
import ChevronIcon from "./ChevronIcon";
import "./Tasks.css";

interface Task extends UserTask {
  externalUrl?: string;
}

interface GroupedTasks {
  [key: string]: Task[];
}

export function Tasks() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [checkingTaskId, setCheckingTaskId] = useState<string | null>(null);
  const [taskCheckError, setTaskCheckError] = useState<{
    id: string;
    message: string;
  } | null>(null);
  const aiTaskAttemptsRef = useRef<Record<string, number>>({});

  const fetchTasks = useCallback(
    async (isRefetch = false) => {
      if (!isRefetch) {
        setIsLoading(true);
      }
      setError(null);

      try {
        const api = ApiService.getInstance();
        const response = await api.api.tasksList();

        if (response && response.tasks) {
          // Filter to only include SC tasks, then transform to include externalUrl
          const tasksWithUrl = response.tasks.map((task) => ({
            ...task,
            // Create a Telegram URL using the externalId, removing any leading '@'
            externalUrl: task.externalId
              ? `https://t.me/${task.externalId.replace(/^@/, "")}`
              : undefined,
          }));

          const onlyAvailableTasks = tasksWithUrl.filter((task) => {
            if (task.type === TaskTypeEnum.AI) {
              return addToHomeScreen.isAvailable();
            }
            return true;
          });

          setTasks(onlyAvailableTasks);
        } else {
          setTasks([]);
          setError(t("toolkit.tasks.errorLoadingData"));
        }
      } catch (err) {
        setError(t("toolkit.tasks.errorLoadingTryAgain"));
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const refetch = useCallback(() => fetchTasks(true), [fetchTasks]);

  const handleOpenExternalLink = useCallback(
    async (task: Task) => {
      if (task.completed) {
        return;
      }

      // Add haptic feedback
      try {
        hapticFeedback.impactOccurred("light");
      } catch (error) {
        // Ignore haptic feedback errors
      }

      if (task.externalUrl) {
        try {
          openTelegramLink(task.externalUrl);
        } catch (error) {
          console.error("Failed to open external link:", error);
        }
      } else if (
        [TaskTypeEnum.FL, TaskTypeEnum.KB, TaskTypeEnum.SL].includes(task.type)
      ) {
        try {
          // Navigate to stage select
          navigate("/stage-select");
        } catch (error) {
          console.error("Failed to navigate to stage select:", error);
        }
      } else if (task.type === TaskTypeEnum.US) {
        try {
          // Navigate to character upgrade page
          navigate("/character-upgrade");
        } catch (error) {
          console.error("Failed to navigate to character upgrade:", error);
        }
      } else if (
        task.type === TaskTypeEnum.BL ||
        task.type === TaskTypeEnum.BB
      ) {
        try {
          // Navigate to shop page
          navigate("/shop");
        } catch (error) {
          console.error("Failed to navigate to shop:", error);
        }
      } else if (task.type === TaskTypeEnum.IF) {
        try {
          // Navigate to invite page
          navigate("/invite");
        } catch (error) {
          console.error("Failed to navigate to invite page:", error);
        }
      } else if (task.type === TaskTypeEnum.AI) {
        addToHomeScreen.ifAvailable();
      }
    },
    [navigate]
  );

  const doLocalChecks = useCallback(async (task: Task) => {
    if (task.type === TaskTypeEnum.AI) {
      // Track attempts for this specific task using ref for immediate updates
      const currentAttempts = aiTaskAttemptsRef.current[task.id] || 0;
      const newAttempts = currentAttempts + 1;

      aiTaskAttemptsRef.current[task.id] = newAttempts;

      // Fail first time, succeed next time
      return newAttempts !== 1;
    }

    return true;
  }, []);

  const handleCheckTask = useCallback(
    async (event: React.MouseEvent, task: Task) => {
      // Prevent the card click handler from triggering
      event.stopPropagation();

      if (!task) return;

      // Clear any previous task check errors
      setTaskCheckError(null);
      setCheckingTaskId(task.id);

      try {
        const isLocalCheckCompleted = await doLocalChecks(task);

        if (!isLocalCheckCompleted) {
          throw new Error(t("toolkit.tasks.errorCheckingTask"));
        }

        const api = ApiService.getInstance();
        const response = await api.api.tasksCheckCreate({ taskId: task.id });

        if (response?.success) {
          // If task is completed, update the local state
          setTasks((prev) =>
            prev.map((prevTask) => {
              if (prevTask.id === task.id) {
                return {
                  ...prevTask,
                  completed: true,
                };
              }
              return prevTask;
            })
          );
        } else {
          throw new Error(response.toString());
        }

        // Refresh tasks list to get the latest progress
        refetch();
      } catch (error) {
        console.error("Failed to check task completion:", error);
        setTaskCheckError({
          id: task.id,
          message: t("toolkit.tasks.errorCheckingTask"),
        });
      } finally {
        setCheckingTaskId(null);
      }
    },
    [refetch, t]
  );

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Group tasks by their group type
  const groupedTasks = useMemo(() => {
    const groups: GroupedTasks = {
      [TaskGroupEnum.DAILY]: [],
      [TaskGroupEnum.SOCIAL]: [],
      [TaskGroupEnum.GAME]: [],
      [TaskGroupEnum.COMPLETED]: [],
    };

    tasks.forEach((task) => {
      groups[task.group] = groups[task.group] || [];
      groups[task.group].push(task);
    });

    return groups;
  }, [tasks]);

  if (isLoading) {
    return (
      <Placeholder
        header={t("toolkit.tasks.loadingHeader")}
        description={t("toolkit.tasks.loadingDescription")}
      />
    );
  }

  if (error) {
    return (
      <Placeholder
        header={t("common.error")}
        description={error}
        action={
          <Button onClick={() => refetch()}>{t("common.tryAgain")}</Button>
        }
      />
    );
  }

  return (
    <div className="tasks-page">
      <div className="tasks-header">
        <h1 className="tasks-title">{t("toolkit.tasks.title")}</h1>
        <p className="tasks-subtitle">{t("toolkit.tasks.subtitle")}</p>
      </div>

      <div className="tasks-list">
        {Object.entries(groupedTasks).map(
          ([group, groupTasks]) =>
            groupTasks.length > 0 && (
              <div key={group} className="task-group">
                <h2 className="task-group-title">
                  {t(`toolkit.tasks.group.${group.toLowerCase()}`)}
                </h2>
                {groupTasks.map((task) => (
                  <div
                    key={task.id}
                    className={`task-card ${
                      task.completed ? "task-card--completed" : ""
                    }`}
                    onClick={() => handleOpenExternalLink(task)}
                  >
                    <div className="task-card-header">
                      <div className="task-content">
                        <h3 className="task-title">{t(task.descriptionKey)}</h3>
                        <div className="task-reward">
                          {task.type === TaskTypeEnum.DR &&
                          task.completed &&
                          task.nextPrize ? (
                            <>
                              {t("toolkit.tasks.tomorrow")} +{task.nextPrize}{" "}
                              <img
                                src="/assets/img/coin.webp"
                                alt="Coin"
                                className="coin-icon"
                              />
                            </>
                          ) : (
                            <>
                              +{task.prize || 100}{" "}
                              <img
                                src="/assets/img/coin.webp"
                                alt="Coin"
                                className="coin-icon"
                              />
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="task-status-icon">
                      {task.completed ? (
                        <div className="check-icon">
                          <CheckIcon />
                        </div>
                      ) : (
                        <div className="chevron-icon">
                          <ChevronIcon />
                        </div>
                      )}
                    </div>

                    {!task.completed && (
                      <div className="task-card-footer">
                        <Button
                          className="check-button"
                          mode="filled"
                          onClick={(e) => handleCheckTask(e, task)}
                          disabled={checkingTaskId === task.id}
                        >
                          {checkingTaskId === task.id
                            ? t("common.checking")
                            : t("common.check")}
                        </Button>
                        {taskCheckError?.id === task.id && (
                          <div className="task-check-error">
                            {taskCheckError.message}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )
        )}

        {Object.values(groupedTasks).every((group) => group.length === 0) && (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <Text className="empty-state-title">
              {t("toolkit.tasks.noTasksAvailable")}
            </Text>
            <Text className="empty-state-message">
              {t("toolkit.tasks.checkBackLater")}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
}
